import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from "react";
import { Box, Typography, TextField, InputAdornment, IconButton, Alert, Tooltip } from "@mui/material";
import { ArrowUpward as SendIcon, ContentCopy as CopyIcon } from "@mui/icons-material";
import ComposerNote from "../ComposerNote";
import { WidgetContext } from "../../store/widgetContext";
import { axiosChat, axiosChannels, axiosFiles } from "../../configs/http-config";
import Sender from "../Sender";
import Receiver from "../Receiver";
import TypingIndicator from "../TypingIndicator";
import AttachmentTray, { ALLOWED_MIME_TYPES, MAX_FILES, MAX_FILE_SIZE_BYTES } from "../Files/AttachmentTray";
import { FILE_STATUS } from "../Files/FileChip";
import { fileScanSSEClient } from "../../services/FileScanSSEClient";
import eventEmitter from "../../utils/eventEmitter";
import { useWidgetMsg } from "../../hooks/widget/useWidgetMsg";
import { streamingManager } from "../../streaming";
import { applyTraceSummary, buildStreamReply, isSummaryEvent, resolveRecordTraceSummary, stripLiveTrace } from "../../streaming/traceRecords";
import { useSession } from "../../session/useSession";
import { SessionManager } from "../../session/SessionManager";
import { generateUUIDv4 } from "../../utils/contactUuid";
import { createPendingRecord, mergePendingRecords } from "../../utils/pendingRecords";
import FeedbackRow from "../Messages/FeedbackRow";
import { canRateRecord, isServerRecordId, replyText } from "../../utils/feedback";
import { readPrompts } from "../../utils/promptLibrary";
import PromptStarters from "./PromptStarters";
import PlusMenu from "./PlusMenu";
import { useScrollAnchor } from "../../hooks/useScrollAnchor";
import { usePasteAttachments } from "../../hooks/usePasteAttachments";
import { composerKeyIntent } from "../../utils/composerKeys";
import { isOperatorsOnly } from "../../utils/responder";
import JumpToLatest from "../JumpToLatest";
import OfflineBanner, { useOnlineStatus } from "../OfflineBanner";
import SendFailed, { isRetryExpired } from "../Messages/SendFailed";
import SuggestedActions from "../Messages/SuggestedActions";
import { chipTurnIndex, isSettledRecord } from "../Messages/turnSlots";
import { chipRecordId, resolveRecordSuggestions } from "../../utils/suggestedActions";

/** How long a touch must rest on a reply before its Copy row shows (chat-experience 11, AC-6). */
export const LONG_PRESS_MS = 500;

/**
 * The text a visitor can copy from a reply: exactly what TextMessage renders, with a trailing
 * streaming cursor stripped. NEVER the correlation id — that is the reference-id control's job.
 */
export const copyableReplyText = (reply) => String(replyText(reply)).replace(/▍$/, "");

/**
 * Whether a record gets chat-experience 11's own "Copy message" row. A reply with text and a server
 * id (a pending echo has only a correlation id), and only when 05a's FeedbackRow is NOT already
 * rendered for it: that row carries its own Copy, and a reply never gets two (11 Step 8).
 */
export const showsCopyMessage = (record, { feedbackRowShown = false } = {}) =>
	!!record?.reply
	&& !record.pending
	&& isServerRecordId(record._id)
	&& !feedbackRowShown
	&& copyableReplyText(record.reply) !== "";

/**
 * Write text to the clipboard. Stale embeds lack `clipboard-write` in the iframe allow list, so a
 * rejected (or missing) Clipboard API falls back to a hidden textarea + `execCommand("copy")`.
 */
export function writeClipboard(value) {
	const fallback = () => {
		const ta = document.createElement("textarea");
		ta.value = value;
		document.body.appendChild(ta);
		ta.select();
		try { document.execCommand("copy"); } catch (_) { /* nothing left to try */ }
		ta.remove();
	};
	if (!navigator.clipboard?.writeText) {
		fallback();
		return;
	}
	navigator.clipboard.writeText(value).catch(fallback);
}

/**
 * The per-record wrapper. The feedback row (05a FR-06) is hidden until the record is hovered or
 * holds focus on a pointer device, always visible on touch (`hover: none`), and stays visible on
 * its own once it holds a rating, an error or the thanks line (`is-sticky`).
 */
export const FEEDBACK_REVEAL_SX = {
	display: "flex",
	flexDirection: "column",
	gap: 1,
	"& .feedback-row": { opacity: 0, transition: "opacity .15s ease" },
	"&:hover .feedback-row, &:focus-within .feedback-row, & .feedback-row.is-sticky": { opacity: 1 },
	"@media (hover: none)": { "& .feedback-row": { opacity: 1 } },
};

export default function Chat({ conversationId, initialAssignedAgents, handleNavigate, setSelectedDocument, setSelectedCollection, ...props }) {
	const { botId, tenantId, projectUUID } = props;
	const { widget, conversations, setConversations, contact, setContact, agents, contactDataCollector, setContactDataCollector } = useContext(WidgetContext);
	const { sessionId } = useSession();
	const inputRef = useRef();
	const pagePropsRef = useRef({ location: "chat", conversationId, assignedAgents: initialAssignedAgents || [] });
	const agentsRef = useRef(null);
	const fileInputRef = useRef(null);
	const messagesRef = useRef(null);
	const [query, setQuery] = useState("");
	const [initialQuery, setInitialQuery] = useState("");
	const [assignedAgentsState, setAssignedAgentsState] = useState([]);
	const [records, setRecords] = useState([]);
	// Messages this widget has sent that the server has not echoed back yet. The POST to
	// /widgets/query answers with an empty ack, so nothing renders from it; the record only
	// arrives over the socket once the backend has persisted it, which sits behind a couple of
	// conversation writes and, on human handoff, an agent lookup and a push notification. Until
	// then the input has been cleared and the message is nowhere, which is the complaint.
	const [pendingRecords, setPendingRecords] = useState([]);
	const [conversation, setConversation] = useState(null);
	const [initialized, setInitialized] = useState(false);
	const initCalledRef = useRef(false);
	const [assistantTyping, setAssistantTyping] = useState(false);
	const [dataCollector, setDataCollector] = useState(null);
	
	const [fieldError, setFieldError] = useState("");
	const [isSending, setIsSending] = useState(false);
	const [attachedFiles, setAttachedFiles] = useState([]);
	// Support/trace reference id state: `sendError` holds the id of a failed send so
	// the user can report it; `copiedRef` drives the "copied" tooltip feedback.
	const [sendError, setSendError] = useState(null);
	const [copiedRef, setCopiedRef] = useState(null);
	// chat-experience 11 (D-20): a failed send keeps its echo on screen with a Retry. The map holds
	// what Retry re-posts, keyed by the correlation id it must reuse; `retrying` greys the button and
	// `retryingRef` stops a double-click from posting twice before that state lands.
	const failedSendsRef = useRef(new Map());
	const retryingRef = useRef(new Set());
	const [retrying, setRetrying] = useState(() => new Set());
	// D-17: the one polite live region's text key, and whether a reply is being announced as running
	// (so the typing dots and the first chunk announce "started" once between them).
	const [announcement, setAnnouncement] = useState("");
	const replyActiveRef = useRef(false);
	// Runtime 09b: the live `suggestions` per recordId (they may land just before or after the stream's
	// `done`, on a second transport — so never on the stream entry), and the records whose chip was
	// already clicked (disabled at once, before the echo retires them).
	const [liveOffers, setLiveOffers] = useState({});
	const [usedOffers, setUsedOffers] = useState(() => new Set());
	const [touchRevealedId, setTouchRevealedId] = useState(null);
	const longPressRef = useRef(null);
	const { online, justReturned } = useOnlineStatus();
	const onlineRef = useRef(online);
	onlineRef.current = online;
	const currentConversation = conversations.find(convo => convo._id === pagePropsRef.current?.conversationId);
	const isResolved = !!(currentConversation?.status === "Resolved" && widget?.allow_resolved_convo_msg === false);
	const { msg, browserLang } = useWidgetMsg();
	// The rating row is always on once the widget config has loaded (owner 2026-10-01, audit lane 12:
	// the trace cutover flag that traces 05a carried as `features.feedbackCapture` is retired). Chat's
	// feedback route is always on too; a refused write is the row's own inline error.
	const feedbackEnabled = Boolean(widget);

	const hasProcessingFiles = attachedFiles.some(f =>
		[FILE_STATUS.UPLOADING, FILE_STATUS.SCANNING].includes(f.scanStatus)
	);
	const readyFiles = attachedFiles.filter(f => f.scanStatus === FILE_STATUS.READY);
	// chat-experience 11: offline joins the one gate. No queue (Q10) — the button simply waits.
	const canSend = online && !isSending && !hasProcessingFiles && (query.trim() !== "" || readyFiles.length > 0);
	const { anchored, unread, onContentChange, anchorNow } = useScrollAnchor(messagesRef);
	const prevRecordCountRef = useRef(0);

	// --- File scan SSE ---
	useEffect(() => {
		const contactId = SessionManager.getSocketContactId()?.replace(/(_[^_]+)\1$/, '$1');
		if (!contactId || !tenantId) return;

		fileScanSSEClient.connect({ tenantId, contactId, botId });

		const unsubscribe = fileScanSSEClient.onStatusUpdate((data) => {
			const { fileId, scanStatus, scanVerdict } = data;
			setAttachedFiles(prev =>
				prev.map(f => f.fileId === fileId ? { ...f, scanStatus, scanVerdict } : f)
			);
		});

		return () => {
			unsubscribe();
			fileScanSSEClient.disconnect();
		};
	}, [tenantId]);

	// --- File upload ---
	const uploadFile = useCallback(async (file) => {
		const contactId = SessionManager.getSocketContactId()?.replace(/(_[^_]+)\1$/, '$1');
		const formData = new FormData();
		formData.append("file", file);

		const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
		const newFileEntry = {
			fileId: tempId,
			originalName: file.name,
			sizeBytes: file.size,
			mimeType: file.type === "image/jpg" ? "image/jpeg" : file.type,
			scanStatus: FILE_STATUS.UPLOADING,
			previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
		};

		setAttachedFiles(prev => [...prev, newFileEntry]);

		try {
			const res = await axiosFiles.post("/files", formData, {
				params: { tenantId, contactId, projectUUID, botId, featureContext: "CHANNEL" },
				headers: { "Content-Type": "multipart/form-data" },
			});

			const { fileId, scanStatus } = res.data?.data || res.data;
			setAttachedFiles(prev =>
				prev.map(f => f.fileId === tempId ? { ...f, fileId, scanStatus: scanStatus || FILE_STATUS.SCANNING } : f)
			);
		} catch (error) {
			console.error("File upload failed:", error);
			setAttachedFiles(prev =>
				prev.map(f => f.fileId === tempId ? { ...f, scanStatus: FILE_STATUS.SCAN_FAILED } : f)
			);
		}
	}, [tenantId, projectUUID]);

	const handleFilesSelected = useCallback((files) => {
		const currentCount = attachedFiles.length;
		const remaining = MAX_FILES - currentCount;

		if (remaining <= 0) {
			setFieldError(msg("file.maxFilesReached", "Maximum {max} files allowed", { max: MAX_FILES }));
			return;
		}

		const filesToProcess = files.slice(0, remaining);

		for (const file of filesToProcess) {
			const normalizedType = file.type === "image/jpg" ? "image/jpeg" : file.type;
			if (!ALLOWED_MIME_TYPES.includes(normalizedType)) {
				setFieldError(msg("file.unsupportedType", "Unsupported file type: {name}", { name: file.name }));
				continue;
			}
			if (file.size > MAX_FILE_SIZE_BYTES) {
				setFieldError(msg("file.tooLarge", "File too large (max 2MB): {name}", { name: file.name }));
				continue;
			}
			setFieldError("");
			uploadFile(file);
		}
	}, [attachedFiles.length, uploadFile, msg]);

	// chat-experience 11: a pasted image/file joins the tray through the same validator as the picker
	// and a drop; the same gate as the attach control (a collector flow, a resolved conversation).
	const { onPaste } = usePasteAttachments({
		onFiles: handleFilesSelected,
		disabled: !!(dataCollector || contactDataCollector) || isResolved,
	});

	const handleFileInputChange = useCallback((e) => {
		const files = Array.from(e.target.files);
		if (files.length > 0) handleFilesSelected(files);
		e.target.value = "";
	}, [handleFilesSelected]);

	const handleRemoveFile = useCallback((fileId) => {
		setAttachedFiles(prev => {
			const file = prev.find(f => f.fileId === fileId);
			if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl);
			return prev.filter(f => f.fileId !== fileId);
		});
	}, []);

	const handleDrop = useCallback((droppedFiles) => {
		handleFilesSelected(droppedFiles);
	}, [handleFilesSelected]);

	// --- Drag-and-drop on the chat area ---
	const [isDragOverChat, setIsDragOverChat] = useState(false);

	const handleChatDragOver = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		if (!isResolved && !dataCollector && !contactDataCollector) {
			setIsDragOverChat(true);
		}
	}, [isResolved, dataCollector, contactDataCollector]);

	const handleChatDragLeave = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOverChat(false);
	}, []);

	const handleChatDrop = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOverChat(false);
		if (isResolved || dataCollector || contactDataCollector) return;
		const files = Array.from(e.dataTransfer.files);
		if (files.length > 0) handleFilesSelected(files);
	}, [isResolved, dataCollector, contactDataCollector, handleFilesSelected]);

	// Copy a reference id to the clipboard and flash a short "copied" confirmation.
	const copyRef = useCallback((id) => {
		if (!id) return;
		writeClipboard(id);
		setCopiedRef(id);
		setTimeout(() => setCopiedRef((prev) => (prev === id ? null : prev)), 1500);
	}, []);

	// Copy an AI reply's rendered text (05a FR-12); the confirmation is keyed by the record id so the
	// same 1.5 s flash drives the row's tooltip.
	const copyText = useCallback((text, id) => {
		if (!text || !id) return;
		writeClipboard(text);
		setCopiedRef(id);
		setTimeout(() => setCopiedRef((prev) => (prev === id ? null : prev)), 1500);
	}, []);

	// D-17: one polite region, state transitions only — never per token, never per second.
	const announceReplyStarted = () => {
		if (replyActiveRef.current) return;
		replyActiveRef.current = true;
		setAnnouncement("reply_started");
	};
	const announceReplyFinished = () => {
		if (!replyActiveRef.current) return;
		replyActiveRef.current = false;
		setAnnouncement("reply_finished");
	};

	// --- Message handling ---
	// `echo` renders the message locally before the server confirms it. Only the paths that
	// carry something the user typed pass it; a session bootstrap or a button payload has
	// nothing to show. `meta` (chat-experience 06c) rides on the body as its own key — channel-service
	// validates `meta.promptStarterId` (≤ 64) and drops it. Runtime 09b's chips add nothing here: a chip
	// click is just its prompt (W15). Distinct from `metadata` (the session blob) and `action_query.meta`.
	// `correlationId` (chat-experience 11) is set only by Retry: the SAME id goes out again, so the
	// channel.inbound dedupe claim drops the second copy if the first did reach the edge, and the
	// server record still replaces the echo it was minted for.
	const handleRequest = async (content, files, { echo = false, meta = null, correlationId: reuseId = null } = {}) => {
		// Mint a per-message support/trace id. Sent as x-correlation-id so the
		// backend adopts it, persists it on the message doc, and propagates it to
		// every downstream service. Generated client-side (not read from the
		// response) so it is known even if the request times out with no reply.
		const correlationId = reuseId ?? generateUUIDv4();
		const prior = reuseId ? failedSendsRef.current.get(reuseId) : null;
		let echoed = !!prior?.echoed;

		if (echo && !reuseId) {
			const text = content?.msg_query?.text;
			if (text) {
				echoed = true;
				setPendingRecords(prev => [ ...prev, createPendingRecord({
					correlationId,
					text,
					conversationId: content?.conversationId ?? null,
				}) ]);
			}
		}

		const markFailed = (failed) => setPendingRecords(prev => (
			prev.some(r => r.correlationId === correlationId && !!r.failed !== failed)
				? prev.map(r => (r.correlationId === correlationId ? { ...r, failed } : r))
				: prev
		));

		try {
			const effectiveContactId = SessionManager.getSocketContactId();
			const metadata = SessionManager.getMetadata();
			const body = {
				uuid: projectUUID,
				contactId: effectiveContactId?.replace(/(_[^_]+)\1$/, '$1'),
				sessionId: sessionId?.replace(/(_[^_]+)\1$/, '$1'),
				metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
				content,
				tenantId,
				botId,
				lang: browserLang,
			};
			if (Array.isArray(files) && files.length > 0) {
				body.files = files;
			}
			if (meta && Object.keys(meta).length > 0) {
				body.meta = meta;
			}
			// Phase-4 CD1: the widget transport is unified-channel-service's (it publishes the
			// message onto channel.inbound); chat 404s /widgets/query now. botId/tenantId stay in
			// the query string — the no-token embed-allowlist fallback reads them there.
			await axiosChannels.post(`/widgets/query?botId=${botId}&tenantId=${tenantId}`, body, {
				headers: { "x-correlation-id": correlationId },
			});
			failedSendsRef.current.delete(correlationId);
			if (echoed) markFailed(false);
			setSendError(prev => (prev && prev.id !== correlationId && reuseId ? prev : null));
		} catch (error) {
			console.error("Error in handleRequest:", error);
			// The echo STAYS: handleSubmit has already cleared the field and nothing restores it, so
			// the visitor's words remain on screen next to the failure with a Retry (D-20). A send with
			// no echo (session bootstrap, collector, button) gets the same row standalone.
			failedSendsRef.current.set(correlationId, {
				content, files, meta, echoed, sentAt: prior?.sentAt ?? Date.now(),
			});
			if (echoed) markFailed(true);
			else setSendError({ id: correlationId });
			replyActiveRef.current = false;
			setAnnouncement("send_failed");
		}
	};

	/** Retry a failed send in place: the same body, the same correlation id, no second echo. */
	const retrySend = async (correlationId) => {
		const payload = failedSendsRef.current.get(correlationId);
		if (!payload || !onlineRef.current || retryingRef.current.has(correlationId)) return;
		if (isRetryExpired(payload.sentAt)) return;
		retryingRef.current.add(correlationId);
		setRetrying(new Set(retryingRef.current));
		anchorNow("smooth");
		try {
			await handleRequest(payload.content, payload.files, { meta: payload.meta, correlationId });
		} finally {
			retryingRef.current.delete(correlationId);
			setRetrying(new Set(retryingRef.current));
		}
	};

	const renderSendFailed = (id) => (
		<SendFailed
			referenceId={id}
			sentAt={failedSendsRef.current.get(id)?.sentAt}
			onRetry={() => retrySend(id)}
			onCopyRef={copyRef}
			copied={copiedRef === id}
			retrying={retrying.has(id)}
			disabled={!online}
			msg={msg}
		/>
	);

	const validateField = (collector, value) => {
		if (!collector || !collector.currAttr) return true;
		const type = collector.currAttr?.type?.toLowerCase();
		const field = collector.currAttr?.name;
		const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

		if (type === "email" || field === "Email") {
			if (!emailPattern.test(value)) {
				return msg("help.invalid_email", "Please enter a valid email address");
			}
		}
		if (type === "number") {
			if (!/^\d+$/.test(value)) {
				return msg("help.invalid_number", "Please enter a valid number");
			}
		}
		return true;
	};

	const handleDataCollector = () => {
		if (!dataCollector) return false;
		const updatedAttrs = dataCollector.innerAttrs.map((attr) =>
			attr.field === dataCollector.currAttr?.field ? { ...attr, value: query.trim() } : attr
		);
		const allAttrsFilled = updatedAttrs.every((attr) => attr.value.trim() !== "");
		setDataCollector((prev) => ({
			...prev,
			innerAttrs: updatedAttrs,
			currAttr: allAttrsFilled ? null : prev.currAttr,
		}));
		setQuery("");
		return true;
	};

	const handleContactDataCollector = () => {
		if (!contactDataCollector) return false;
		const updatedAttrs = contactDataCollector.innerAttrs.map((attr) =>
			attr.field === contactDataCollector.currAttr?.field
				? { ...attr, value: query.trim() }
				: attr
		);
		const nextAttr = updatedAttrs.find((attr) => !attr.value);
		setQuery("");
		if (!nextAttr) {
			const { content } = contactDataCollector;
			setContactDataCollector(null);
			handleRequest({
				conversationId: pagePropsRef.current?.conversationId,
				action_query: {
					intent: content.config.intent,
					prevQuery: content?.flow === 6 ? null : initialQuery,
					nextWorkflowId: content?.config?.nextWorkflowId,
					meta: content,
					data: {
						attrs: updatedAttrs
					}
				},
			});
			setInitialQuery("");
		} else {
			setContactDataCollector((prev) => ({
				...prev,
				innerAttrs: updatedAttrs,
				currAttr: nextAttr,
			}));
		}
		return true;
	};

	const handleDefaultMessage = async () => {
		const conversationId = pagePropsRef.current?.conversationId;
		const fileIds = readyFiles.map(f => f.fileId);

		const content = {
			conversationId: conversationId || null,
			msg_query: { text: query },
		};

		await handleRequest(content, fileIds, { echo: true });
	};

	// chat-experience 06c (D-34): ONE handler for the starters list and the `+` → Prompts view. A
	// selected prompt is sent at once — never placed in the composer, never routed through
	// handleSubmit (it reads `query`, which a tap never sets).
	const handlePromptSelect = async (entry) => {
		if (isSending || isResolved || !online) return;
		setIsSending(true);
		anchorNow("smooth");
		try {
			if (!initialQuery) setInitialQuery(entry.prompt);
			await handleRequest(
				{ conversationId: pagePropsRef.current?.conversationId || null, msg_query: { text: entry.prompt } },
				[],
				{ echo: true, meta: { promptStarterId: entry.id } },
			);
		} finally {
			setIsSending(false);
		}
	};

	// Runtime 09b (W15): a chip sends ONLY its prompt, as the visitor's next message — echoed, retried
	// like any typed send, gated like a starter (CX06c), and re-anchoring first (D-16).
	const handleSuggestedAction = async (recordId, action) => {
		if (isSending || isResolved || !online || usedOffers.has(recordId)) return;
		setUsedOffers(prev => new Set(prev).add(recordId));
		setIsSending(true);
		anchorNow("smooth");
		try {
			await handleRequest(
				{ conversationId: pagePropsRef.current?.conversationId || null, msg_query: { text: action.prompt } },
				[],
				{ echo: true },
			);
		} finally {
			setIsSending(false);
		}
	};

	const handleSubmit = async () => {
		const input = query.trim();
		const hasReadyFiles = readyFiles.length > 0;

		if (!input && !hasReadyFiles) {
			setQuery("");
			return;
		}

		if (!canSend) return;

		setIsSending(true);

		if (input && !initialQuery && !contactDataCollector) {
			setInitialQuery(input);
		}

		if (input) {
			if (contactDataCollector) {
				const errorMessage = validateField(contactDataCollector, input);
				if (typeof errorMessage === "string") {
					setFieldError(errorMessage);
					setIsSending(false);
					return;
				}
			}
			if (dataCollector) {
				const errorMessage = validateField(dataCollector, input);
				if (typeof errorMessage === "string") {
					setFieldError(errorMessage);
					setIsSending(false);
					return;
				}
			}
			setFieldError("");
		}

		try {
			if (input && !hasReadyFiles) {
				if (handleDataCollector()) { setIsSending(false); return; }
				if (handleContactDataCollector()) { setIsSending(false); return; }
			}
			// D-16: the visitor's own send always re-anchors, whatever they were reading.
			anchorNow("smooth");
			await handleDefaultMessage();
			setQuery("");
			setAttachedFiles(prev => {
				prev.forEach(f => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); });
				return [];
			});
		} catch (error) {
			console.error("Error in handleSubmit:", error);
		} finally {
			setIsSending(false);
		}
	};

	const initSessionAndGetContact = async (_records) => {
		if (_records.length === 0 && !initCalledRef.current) {
			initCalledRef.current = true;
			await handleRequest({
				conversationId: pagePropsRef.current?.conversationId || null,
				new_session: true,
			});
		}
	};

	const handleAssistantTyping = (message) => {
		const { conversationId } = message;
		// Runtime 08c: a reply that already shows its live trace block (or text) needs no dots too.
		if (pagePropsRef.current?.conversationId === conversationId && !streamingManager.hasLiveStream(conversationId)) {
			setAssistantTyping(true);
			announceReplyStarted();
		}
	};

	const updateCollectorState = (reply, setCollector, record) => {
		if (!reply) return;
		const attrs = reply?.config?.attrs || reply.attrs;
		if (!Array.isArray(attrs) || attrs.length === 0) return;
		setCollector((prev) => {
			const innerAttrs = attrs.map((attr) => ({ ...attr, value: attr?.value || "" }));
			const currentAttr = innerAttrs.find((attr) => !attr.value);
			return {
				...prev,
				content: reply,
				innerAttrs,
				currAttr: currentAttr,
				recordId: record._id,
			};
		});
	};

	const isValidRecord = (rec) => {
		if (rec.query?.type === "msg") return true;
		if (rec.reply?.type === "contact_data_collector") return true;
		if (["ticket_data_collector", "connect_data_collector"].includes(rec.reply?.type) && Array.isArray(rec.reply?.config?.attrs)) {
			const hasEmptyAttr = rec.reply.config.attrs.some(attr => !attr.value);
			if (!hasEmptyAttr) return false;
		}
		if (!rec.reply && !rec.query?.config?.message) {
			return false;
		}
		return true;
	};

	function updateConversationRecords(prev, id, updater) {
		const exists = prev.some(conv => conv._id === id);
		if (!exists) {
			return [...prev, {
				_id: id,
				records: updater([]).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),
				placeholder: false,
				status: "Open",
			}];
		}
		return prev.map(conv => {
			if (conv._id !== id) return conv;
			return {
				...conv,
				records: updater(conv.records || []).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),
			};
		});
	}

	function upsertRecord(records, newRecord) {
		const index = records.findIndex(r => r._id === newRecord._id);
		if (index === -1) {
			return [...records, newRecord];
		}
		return records.map(r => (r._id === newRecord._id ? newRecord : r));
	}

	function updateRecordReply(records, recordId, replyUpdater) {
		const exists = records.some(r => r._id === recordId);
		if (!exists) {
			return [
				...records,
				{
					_id: recordId,
					query: null,
					reply: replyUpdater({}),
				},
			];
		}
		return records.map(r =>
			r._id === recordId
				? { ...r, reply: replyUpdater(r.reply || {}) }
				: r
		);
	}

	const handleRecordUpdate = (message) => {
		const { contactId, record } = message;
		const fullContactId = SessionManager.getSocketContactId();
		if (contactId !== fullContactId) return;
		const stream = streamingManager.get(record._id);
		// The persisted record is authoritative (runtime 08c §8): no live trace survives it.
		let finalRecord = stripLiveTrace(record);
		if (stream?.text) {
			finalRecord = {
				...finalRecord,
				reply: {
					...(finalRecord.reply || {}),
					message: stream.text,
					streaming: false,
				},
			};
		}
		if (stream) streamingManager.remove(record._id);
		setConversations(prev =>
			updateConversationRecords(prev, finalRecord.conversationId, records =>
				upsertRecord(records.filter(isValidRecord), finalRecord)
			)
		);
		if (!pagePropsRef.current?.conversationId && finalRecord.conversationId) {
			pagePropsRef.current = { ...pagePropsRef.current, conversationId: finalRecord.conversationId };
			if (handleNavigate) {
				handleNavigate({ conversationId: finalRecord.conversationId, assignedAgents: assignedAgentsState });
			}
		}
		if (["ticket_data_collector", "connect_data_collector"].includes(finalRecord.reply?.type)) {
			updateCollectorState(finalRecord.reply, setDataCollector, finalRecord);
		}
		if (finalRecord.reply?.type === "contact_data_collector") {
			updateCollectorState(finalRecord.reply, setContactDataCollector, finalRecord);
		}
		setAssistantTyping(false);
		if (finalRecord.reply && finalRecord.reply.streaming !== true) announceReplyFinished();
	};

	const handleRecordUpdateStream = (message) => {
		// The socket is the single source for streaming chunks. There used to be a second, parallel
		// SSE transport here and this handler picked whichever arrived first per recordId (`_source`)
		// and dropped the other; the SSE half was deleted server-side (it could not cross replicas)
		// so there is nothing left to dedup against.
		// Runtime 08c: a protocol-2 session also gets `trace` (labels only) and `suggestions` on this
		// topic, on messages without `chunk`/`done`. Anything else unknown is ignored (D-L66).
		const { recordId, conversationId, chunk, done, fullMessage, agentIDaasId, trace, suggestions } = message;
		if (!recordId || conversationId !== pagePropsRef.current?.conversationId) return;
		// Runtime 09b: `suggestions` arrive on their own message, keyed by recordId, whether or not the
		// stream is still open — kept beside the record, never reopening (or creating) a stream entry.
		if (Array.isArray(suggestions)) {
			setLiveOffers(prev => ({ ...prev, [recordId]: suggestions }));
			if (chunk === undefined && !done && !trace) return;
		}

		const prevState = streamingManager.get(recordId);
		// Chat sends the `summary` after the stream's `done`: it lands on the finished reply and
		// never reopens the stream.
		if (!prevState && isSummaryEvent(trace)) {
			setConversations(prev => applyTraceSummary(prev, conversationId, recordId, trace));
			return;
		}
		const streamState = streamingManager.ingest({ recordId, conversationId, chunk, done, trace });
		if (!streamState) return;
		// The first chunk OR the first trace event replaces the typing dots.
		if (!prevState?.started && streamState.started) {
			setAssistantTyping(false);
			announceReplyStarted();
		}
		if (!streamState.started) return;
		let finalText = streamState.text;
		if (done && fullMessage) {
			try { finalText = JSON.parse(fullMessage).response || streamState.text; } catch (_) { }
		}
		setConversations(prev =>
			updateConversationRecords(prev, conversationId, records =>
				updateRecordReply(records, recordId, prevReply =>
					buildStreamReply(prevReply, streamState, { done, finalText, fullMessage, agentIDaasId })
				)
			)
		);
		if (done) {
			setAssistantTyping(false);
			streamingManager.remove(recordId);
			announceReplyFinished();
		}
	};

	const handleContactUpdate = (message) => {
		const { contact: updatedContact } = message;
		if (updatedContact) {
			setContact(updatedContact);
		}
	};

	const updateAssignedAgents = (assignees) => {
		if (!Array.isArray(assignees) || assignees.length === 0) {
			console.warn("updateAssignedAgents: No valid assignees, skipping update.");
			return;
		}
		const updatedAgents = assignees
			.map((assigneeId) => agentsRef.current.find((agent) => agent.agentIDaasId === assigneeId))
			.filter(Boolean);
		setAssignedAgentsState(updatedAgents);
	};

	const handleConvAssignee = (message) => {
		const { assignees, newConvId, prevConvId } = message;
		if (!prevConvId || !newConvId) {
			console.error("handleConvAssignee: Invalid conversation IDs", message);
			return;
		}
		if (Array.isArray(assignees)) {
			updateAssignedAgents(assignees);
		}
		setConversations((prevConversations) =>
			prevConversations.map((conversation) => {
				if (conversation._id === prevConvId) {
					return {
						...conversation,
						_id: newConvId,
						records: (conversation.records || []).map(record => ({
							...record,
							conversationId: newConvId
						}))
					};
				}
				return conversation;
			})
		);
		if (
			pagePropsRef.current?.location === "chat" &&
			pagePropsRef.current?.conversationId === prevConvId
		) {
			const updatedAgents = assignees
				.map((assigneeId) => agentsRef.current?.find((agent) => agent.agentIDaasId === assigneeId))
				.filter(Boolean);

			const updatedPage = { ...pagePropsRef.current, conversationId: newConvId, assignedAgents: updatedAgents };

			pagePropsRef.current = updatedPage;
			setAssignedAgentsState(updatedAgents);

			if (handleNavigate) {
				handleNavigate({ conversationId: newConvId, assignedAgents: updatedAgents });
			}
		}
	};

	const resolveMessage = (reply) => {
		if (!reply) return "";

		return (
			reply._streamText ??
			reply.message ??
			reply.config?.message ??
			""
		);
	};

	useEffect(() => {
		pagePropsRef.current = { ...pagePropsRef.current, conversationId, assignedAgents: initialAssignedAgents || [] };
	}, [conversationId, initialAssignedAgents]);

	useEffect(() => {
		if (conversations && pagePropsRef.current) {
			if (pagePropsRef.current.conversationId) {
				const conversation = conversations.find(conv => conv._id === pagePropsRef.current.conversationId);
				setConversation(conversation);
				const _records = conversation?.records || [];
				setRecords(_records);
				if (conversation?.placeholder === true && !initialized) {
					initSessionAndGetContact(_records);
					setInitialized(true);
				}
			} else {
				setConversation(null);
				const existingPlaceholder = conversations.find(conv => conv.placeholder === true && conv.status !== "Resolved");
				if (existingPlaceholder) {
					const _records = existingPlaceholder?.records || [];
					if (Array.isArray(_records) && _records.length === 0) {
						initSessionAndGetContact([]);
					} else {
						setRecords(_records);
					}
				} else if (!initialized) {
					initSessionAndGetContact([]);
				}
				setInitialized(true);
			}
		}
	}, [conversations, pagePropsRef]);

	useEffect(() => {
		if (agents) {
			agentsRef.current = agents;
			const conversation = conversations?.find((conv) => conv._id === pagePropsRef.current?.conversationId);
			if (conversation?.assignees) {
				updateAssignedAgents(conversation.assignees);
			} else if (pagePropsRef.current?.assignedAgents?.length) {
				setAssignedAgentsState(pagePropsRef.current.assignedAgents);
			}
		}
	}, [agents, conversations, pagePropsRef]);

	useEffect(() => {
		if (handleNavigate && pagePropsRef.current?.conversationId) {
			handleNavigate({ conversationId: pagePropsRef.current.conversationId, assignedAgents: assignedAgentsState });
		}
	}, [assignedAgentsState, handleNavigate]);

	useEffect(() => {
		if (!dataCollector) return;
		const nextAttr = dataCollector.innerAttrs.find((attr) => !attr.value);
		if (nextAttr && nextAttr !== dataCollector.currAttr) {
			setDataCollector((prev) => ({
				...prev,
				currAttr: nextAttr,
			}));
		} else if (!nextAttr) {
			const content = dataCollector.content;
			handleRequest({
				conversationId: pagePropsRef.current?.conversationId,
				action_query: {
					actionId: content?.actionId,
					intent: content.config.intent,
					meta: content,
					data: {
						attrs: dataCollector.innerAttrs
					}
				},
			});
			setDataCollector(null);
		}
	}, [dataCollector]);

	useEffect(() => {
		if (!contactDataCollector) return;
		const remainingAttrs = contactDataCollector.innerAttrs.filter(
			(attr) => !(attr.field in (contact?.meta || {})) || contact.meta[attr.field] === ""
		);
		const nextAttr = remainingAttrs.find((attr) => !attr.value);

		if (nextAttr && nextAttr !== contactDataCollector.currAttr) {
			setContactDataCollector((prev) => ({
				...prev,
				currAttr: nextAttr,
				innerAttrs: remainingAttrs,
			}));
		} else if (!nextAttr) {
			setContactDataCollector(null);
		}
	}, [contactDataCollector, contact?.meta]);

	const visibleRecords = useMemo(
		() => mergePendingRecords(records, pendingRecords, pagePropsRef.current?.conversationId ?? null),
		[records, pendingRecords]
	);

	// chat-experience 06c — D-25 in one expression: the starters exist only while the transcript is
	// empty (the local echo counts, so a tap removes them on the same render), and 09b's chips only hang
	// off a reply. The parent's literal rule; Q15 would change only this line.
	const showStarters = !isResolved && visibleRecords.length === 0;
	// Runtime 09b: chips render on the latest record only, once its reply is finished.
	const chipRecord = useMemo(() => chipRecordId(visibleRecords), [visibleRecords]);
	// spec-chat-experience 15 (§5.9 rule 2): the one turn chips may hang off — the newest, once settled.
	const lastSettledTurn = useMemo(() => chipTurnIndex(visibleRecords, isSettledRecord), [visibleRecords]);
	const promptLibrary = useMemo(() => readPrompts(widget), [widget]);

	// chat-experience 11 (D-16): every scroll goes through the anchor, which follows only a reader who
	// is already at the bottom. New records count toward the pill's badge; a stream growing an existing
	// record (`added` 0) follows with 'auto', a settled record with 'smooth'.
	useEffect(() => {
		const count = visibleRecords.length;
		const added = Math.max(0, count - prevRecordCountRef.current);
		prevRecordCountRef.current = count;
		if (count === 0) return;
		onContentChange({ added, behavior: added > 0 ? "smooth" : "auto" });
	}, [visibleRecords, onContentChange]);
	// A collector, an inline error or the typing dots are not messages: no badge, and never a scroll
	// for a reader who is not at the bottom.
	useEffect(() => { onContentChange({ countUnread: false, behavior: "smooth" }); }, [contactDataCollector, dataCollector]);
	useEffect(() => { if (fieldError) onContentChange({ countUnread: false, behavior: "smooth" }); }, [fieldError]);
	useEffect(() => { if (assistantTyping) onContentChange({ countUnread: false, behavior: "smooth" }); }, [assistantTyping]);
	// A long-press reveal lasts until the next record arrives.
	useEffect(() => { setTouchRevealedId(null); }, [visibleRecords.length]);
	useEffect(() => () => clearTimeout(longPressRef.current), []);

	// Touch reveal of the Copy row: a 500 ms press on the record; lifting, moving or cancelling earlier
	// is a tap or a scroll, and a touch on any other record hides a row revealed before. `id` is null
	// for a record with no Copy row: touching it only clears.
	const longPressHandlers = (id) => ({
		onTouchStart: () => {
			clearTimeout(longPressRef.current);
			setTouchRevealedId(prev => (id && prev === id ? prev : null));
			if (id) longPressRef.current = setTimeout(() => setTouchRevealedId(id), LONG_PRESS_MS);
		},
		onTouchEnd: () => clearTimeout(longPressRef.current),
		onTouchMove: () => clearTimeout(longPressRef.current),
		onTouchCancel: () => clearTimeout(longPressRef.current),
	});

	const handleComposerKeyDown = (e) => {
		const intent = composerKeyIntent(e);
		if (intent === "send") {
			e.preventDefault();
			if (canSend) handleSubmit();   // not ready ⇒ swallowed, no hint
		} else if (intent === "dismiss") {
			setFieldError("");
		}
	};

	const announcementText = {
		reply_started: msg("chat.reply_started", "Assistant is replying"),
		reply_finished: msg("chat.reply_finished", "Reply received"),
		send_failed: msg("chat.send_failed", "Message not sent"),
	}[announcement] ?? "";

	useEffect(() => {
		const handleSocketReconnected = async () => {
			const rawContactId = SessionManager.getSocketContactId()?.replace(/(_[^_]+)\1$/, '$1');
			if (!rawContactId) return;
			try {
				const res = await axiosChat.get(`/conversations/all/${rawContactId}`, { params: { tenantId, botId } });
				const rawData = res.data?.data;
				const freshConversations = rawData?.conversations || (Array.isArray(rawData) ? rawData : []);
				if (freshConversations.length === 0) return;

				const openConversations = freshConversations.filter(c => c.status === "Open");
				const latest = [...openConversations].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
				if (latest && latest._id !== pagePropsRef.current?.conversationId) {
					handleConvAssignee({
						prevConvId: pagePropsRef.current?.conversationId,
						newConvId: latest._id,
						assignees: latest.assignees || [],
					});
				}

				const activeConvId = pagePropsRef.current?.conversationId;
				const serverConv = freshConversations.find(c => c._id === activeConvId);
				if (serverConv?.records?.length) {
					setConversations(prev =>
						updateConversationRecords(prev, activeConvId, localRecords => {
							const localMap = new Map(localRecords.map(r => [r._id, r]));
							let changed = false;
							const merged = localRecords.map(lr => {
								const sr = serverConv.records.find(s => s._id === lr._id);
								if (!sr || lr.streaming) return lr;
								if (!lr.reply && sr.reply) { changed = true; return sr; }
								return lr;
							});
							const missed = serverConv.records.filter(sr => !localMap.has(sr._id));
							if (missed.length > 0) changed = true;
							return changed ? [...merged, ...missed] : localRecords;
						})
					);
				}
			} catch (e) {
				console.warn("@@ Chat @@ Sync after reconnect failed", e);
			}
		};

		const listeners = [
			["assistant_typing", handleAssistantTyping],
			["record_update", handleRecordUpdate],
			["conversation_assignee", handleConvAssignee],
			["contact_update", handleContactUpdate],
			["record_update_stream", handleRecordUpdateStream],
			["socket_reconnected", handleSocketReconnected],
		];

		listeners.forEach(([event, handler]) => {
			eventEmitter.on(event, handler);
		});

		return () => {
			listeners.forEach(([event, handler]) => {
				eventEmitter.off(event, handler);
			});
		};
	}, []);

	return (
		<Box
			className="modern-chat-container"
			onDragOver={handleChatDragOver}
			onDragLeave={handleChatDragLeave}
			onDrop={handleChatDrop}
			sx={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				background: "white",
				overflow: "hidden",
				position: "relative",
				"& .sender-ui > .sender-message-container": {
					background: "black !important",
				},
				// The AI reply sits flat on the transcript — no shadow (owner 2026-10-03).
				"& .markdown-message-box": {
					background: "white !important",
					boxShadow: "none !important",
				},
			}}
		>
			{isDragOverChat && (
				<Box
					sx={{
						position: "absolute",
						inset: 0,
						zIndex: 100,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						backgroundColor: "rgba(178, 110, 252, 0.08)",
						border: "2px dashed #B26EFC",
						borderRadius: "12px",
						pointerEvents: "none",
					}}
				>
					<Typography sx={{ color: "#B26EFC", fontWeight: 500, fontSize: "14px" }}>
						{msg("file.dragAndDrop", "Drop files here")}
					</Typography>
				</Box>
			)}

			{conversation && ["Telegram", "Whatsapp", "Messenger", "Viber"].includes(conversation.origin) ? (
				<Alert severity="warning">
					This conversation is from {conversation.origin}. Please use {conversation.origin} Messenger to reply to this conversation.
				</Alert>
			) : (
				<>
					<OfflineBanner online={online} justReturned={justReturned} msg={msg} />
					{/* D-17: `role="log"` with its implicit live region switched OFF — several screen readers
					    would otherwise read every streamed token. The one polite region below announces
					    state transitions only. */}
					<Box
						ref={messagesRef}
						role="log"
						aria-live="off"
						aria-label={msg("conversations.title", "Conversations")}
						sx={{
							flex: 1,
							overflowY: "auto",
							padding: "20px 20px 10px 20px",
							background: "white",
							display: "flex",
							flexDirection: "column",
							gap: 2,
						}}
					>
						{showStarters && <PromptStarters onSelect={handlePromptSelect} disabled={isSending || !online} />}
						{visibleRecords && visibleRecords.map((record, recordIdx) => {
							const feedbackRowShown = feedbackEnabled && canRateRecord(record, pagePropsRef.current?.conversationId);
							const copyMessage = showsCopyMessage(record, { feedbackRowShown });
							const isLastSettledTurn = recordIdx === lastSettledTurn;
							// spec-chat-experience 15: 05a's thumbs + Copy (or 11's Copy alone) are the turn's slot 5 —
							// one row, INSIDE the turn and before the chips; the reference-id control stays below it.
							const feedbackSlot = feedbackRowShown ? (
								<FeedbackRow
									record={record}
									conversationId={pagePropsRef.current?.conversationId}
									onCopy={copyText}
									copiedId={copiedRef}
								/>
							) : copyMessage ? (
								<Box
									className="widget-msg-actions"
									data-revealed={touchRevealedId === record._id ? "true" : undefined}
								>
									<Tooltip title={copiedRef === record._id ? msg("chat.copied", "Copied") : msg("chat.copy_message", "Copy message")}>
										<IconButton
											size="small"
											aria-label={msg("chat.copy_message", "Copy message")}
											data-action="copy-message"
											data-copied={copiedRef === record._id ? "true" : undefined}
											onClick={() => copyText(copyableReplyText(record.reply), record._id)}
											sx={{ color: "#808080", p: 0.25, "&:hover": { color: "#B26EFC" } }}
										>
											<CopyIcon sx={{ fontSize: 13 }} />
										</IconButton>
									</Tooltip>
								</Box>
							) : null;
							return (
							// Keyed on the correlation id where there is one, so a local echo and the
							// record that replaces it are the same element to React. Keying on _id made
							// the swap an unmount and a remount, which replayed the entrance animation:
							// the message dropped out and slid back in.
								<Box
									key={record.correlationId || record._id}
									className="widget-record"
									sx={FEEDBACK_REVEAL_SX}
									{...longPressHandlers(copyMessage ? record._id : null)}
								>
									{record.query && (
										<Sender content={record.query} files={record.files} />
									)}
									{record.reply && (
										<Receiver
											content={record.reply}
											agents={agents}
											conversationId={pagePropsRef.current?.conversationId}
											contact={contact}
											contactDataCollector={contactDataCollector}
											dataCollector={dataCollector}
											recordId={record._id}
											traceSummary={resolveRecordTraceSummary(record)}
											handleRequest={handleRequest}
											handleNavigate={handleNavigate}
											setSelectedDocument={setSelectedDocument}
											setSelectedCollection={setSelectedCollection}
											widget={widget}
											resolveMessage={resolveMessage}
											{...props}
											isLastSettledTurn={isLastSettledTurn}
											feedbackSlot={feedbackSlot}
										/>
									)}
									{/* Slot 6, outside the turn: the newest settled reply only (09b keeps its type rule). */}
									{isLastSettledTurn && record._id === chipRecord && (
										<SuggestedActions
											actions={resolveRecordSuggestions(record, liveOffers[record._id])}
											disabled={isSending || isResolved || !online || usedOffers.has(record._id)}
											onSelect={(action) => handleSuggestedAction(record._id, action)}
										/>
									)}
									{record.pending && record.failed && renderSendFailed(record.correlationId)}
									{record.correlationId && !(record.pending && record.failed) && (
										<Box sx={{ display: "flex", justifyContent: "flex-end", mt: -0.5 }}>
											<Tooltip title={copiedRef === record.correlationId ? msg("chat.ref_copied", "Reference copied") : msg("chat.copy_ref", "Copy reference ID")}>
												<IconButton
													size="small"
													onClick={() => copyRef(record.correlationId)}
													sx={{ color: "#C0C0C0", p: 0.25, "&:hover": { color: "#B26EFC" } }}
												>
													<CopyIcon sx={{ fontSize: 13 }} />
												</IconButton>
											</Tooltip>
										</Box>
									)}
								</Box>
							);
						})}
						{assistantTyping && <TypingIndicator />}
						{fieldError && (
							<Typography variant="caption" sx={{ color: "#d32f2f", fontSize: "12px" }}>
								{fieldError}
							</Typography>
						)}
						{/* A failure with no echo to hang a row on (session bootstrap, collector, button). */}
						{sendError && renderSendFailed(sendError.id)}
					</Box>
					<div className="widget-sr-only" aria-live="polite" aria-atomic="true">{announcementText}</div>

					<Box sx={{ position: "relative", height: 0 }}>
						<JumpToLatest visible={!anchored} unread={unread} onClick={() => anchorNow("smooth")} msg={msg} />
					</Box>
					<Box sx={{ padding: "12px 20px" }}>
						{isResolved ? (
							<ComposerNote>
								{msg("conversations.ended_conversation", "This conversation has ended, please start a new conversation.")}
							</ComposerNote>
						) : (
							<>
								{attachedFiles.length > 0 && (
									<AttachmentTray
										files={attachedFiles}
										onRemove={handleRemoveFile}
										onDrop={handleDrop}
										msg={msg}
										disabled={!!(dataCollector || contactDataCollector)}
									/>
								)}
								<TextField
									fullWidth
									multiline
									minRows={1}
									maxRows={4}
									inputRef={inputRef}
									placeholder={
										dataCollector?.currAttr?.name ||
										contactDataCollector?.currAttr?.name ||
										msg("conversations.input_placeholder", "Ask me anything")
									}
									value={query}
									onChange={(e) => setQuery(e.target.value)}
									onKeyDown={handleComposerKeyDown}
									onPaste={onPaste}
									size="small"
									sx={{
										"& .MuiOutlinedInput-root": {
											borderRadius: "16px",
											background: "white",
											border: "1px solid #E8E8E8",
											"& fieldset": {
												border: "none",
											},
										},
									}}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<PlusMenu
													prompts={promptLibrary}
													onUploadClick={() => fileInputRef.current?.click()}
													onPromptSelect={handlePromptSelect}
													uploadDisabled={attachedFiles.length >= MAX_FILES}
													disabled={!!(dataCollector || contactDataCollector)}
												/>
												<input
													ref={fileInputRef}
													type="file"
													multiple
													accept={ALLOWED_MIME_TYPES.join(",")}
													style={{ display: "none" }}
													onChange={handleFileInputChange}
												/>
											</InputAdornment>
										),
										endAdornment: (
											<InputAdornment position="end">
												{/* D-35: the console's send control — one filled circle with an upward arrow,
												    last in the row. The widget is send-only (no Stop on a customer surface); the
												    brand lives in the fill, never in a different glyph. The <span> lets the
												    offline tooltip fire on a disabled button. */}
												<Tooltip title={online ? "" : msg("chat.offline", "You are offline")}>
													<span className="widget-send-control">
														<IconButton
															size="small"
															onClick={handleSubmit}
															disabled={!canSend}
															aria-label={msg("chat.send", "Send")}
															sx={{
																width: 30,
																height: 30,
																borderRadius: "50%",
																flexShrink: 0,
																backgroundColor: canSend ? "#B26EFC" : "#808080",
																color: "#FFFFFF",
																transition: "background-color 0.15s",
																"&:hover": { backgroundColor: "#B26EFC", opacity: 0.85 },
																"&.Mui-disabled": { backgroundColor: "#808080", color: "#FFFFFF" },
															}}
														>
															<SendIcon sx={{ fontSize: 16 }} />
														</IconButton>
													</span>
												</Tooltip>
											</InputAdornment>
										),
									}}
								/>
								{/* An "Operators only" channel has no AI to warn about (86bcj1kba). */}
								{!isOperatorsOnly(widget) && (
									<ComposerNote>
										{msg("conversations.ai_disclaimer", "The AI agent can make mistakes. Verify important information.")}
									</ComposerNote>
								)}
							</>
						)}
					</Box>
				</>
			)}
		</Box>
	);
}
